import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { LuFileText, LuCircleCheck } from 'react-icons/lu';
import api from '../../services/api';
import { getTodayISO } from '../../utils/dateUtils';
import ModalShell from '../ui/ModalShell';
import ConfirmDialog from '../ui/ConfirmDialog';
import { generarPDFCierreMensual, nombreMes } from '../../utils/pdf/cierreMensual';
import TarifaTramos from './cierre/TarifaTramos';
import EquiposDelMes from './cierre/EquiposDelMes';
import PeriodoCierre from './cierre/PeriodoCierre';
import RepuestosComprados from './cierre/RepuestosComprados';

// Cierre mensual de un cliente con tarifa por volumen (MODO AGUA). Rehecho el 14-oct-2026 para
// seguir el camino de todos los meses, de arriba hacia abajo:
//   1. el mes ya calculado (sin "Calcular"), con cuánto falta para el tramo siguiente;
//   2. lo que respondió la empresa en su portal (informativo: se puede facturar igual);
//   3. los equipos del mes para revisar (el observado, en rojo; tocar abre el trabajo);
//   4. facturar (N° + PDF) → cobrado.
// La tarifa queda abajo en una línea, con "Editar".
// 22-oct-2026: se puede cerrar un período con fechas (no solo el mes) y sumar repuestos comprados.

const fmt = v => `$ ${Math.round(Number(v || 0)).toLocaleString('es-AR')}`;
const mesActual = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`; };
const ddmm = f => { const [, m, d] = String(f).split('-'); return `${d}/${m}`; };
const PASO = 'text-label font-black text-muted uppercase tracking-widest mb-1.5 px-1';

const DESHACER = 'mt-1 text-label font-black text-secondary underline';

function EstadoEmpresa({ aprob, onDeshacer }) {
    if (!aprob) return <p className="px-3 py-2.5 rounded-xl bg-panel text-caption text-muted">La empresa todavía no revisó el resumen de este mes. Podés facturar igual.</p>;
    if (aprob.estado === 'RESPONDIDO') {
        return (
            <div className="px-3 py-2.5 rounded-xl bg-panel text-caption">
                <p className="font-black text-ink">Le respondiste las observaciones</p>
                <p className="text-muted mt-0.5">Le llegó un aviso: esperando que lo apruebe de nuevo. Podés facturar igual.</p>
                <button type="button" onClick={onDeshacer} className={DESHACER}>Volver a "sin revisar"</button>
            </div>
        );
    }
    const ok = aprob.estado === 'APROBADO';
    return (
        <div className={`px-3 py-2.5 rounded-xl text-caption ${ok ? 'bg-[rgba(22,163,74,0.1)] text-[#16A34A]' : 'bg-[rgba(201,52,31,0.1)] text-brand-red'}`}>
            <p className="font-black">{ok ? '✓ La empresa aprobó el resumen' : `La empresa observó el resumen${aprob.observados?.length ? `: ${aprob.observados.length} equipo${aprob.observados.length !== 1 ? 's' : ''} que no reconoce (marcados abajo)` : ''}`}</p>
            {aprob.comentario && <p className="text-ink mt-0.5">“{aprob.comentario}”</p>}
            <p className="text-muted mt-0.5">{[aprob.automatico ? 'Aprobado solo por vencer el plazo' : aprob.usuario, aprob.fecha ? new Date(aprob.fecha).toLocaleDateString('es-AR') : null].filter(Boolean).join(' · ')}</p>
            <button type="button" onClick={onDeshacer} className={DESHACER}>Deshacer</button>
        </div>
    );
}

export default function CierreMensualModal({ cliente, mesInicial = null, periodoInicial = null, onClose, onAbrirServicio, onEditarServicio, onCambio, onMes }) {
    const [mes, setMes] = useState(mesInicial || mesActual());
    // { desde, hasta } o null = mes calendario. Desde Trabajos abre el período del renglón (25-oct-2026)
    const [periodo, setPeriodo] = useState(periodoInicial?.desde && periodoInicial?.hasta ? periodoInicial : null);
    const [cierre, setCierre] = useState(null);
    const [aprob, setAprob] = useState(null);
    const [cargando, setCargando] = useState(true);
    const [factura, setFactura] = useState('');
    const [marcando, setMarcando] = useState(false);
    const [confirmar, setConfirmar] = useState(null); // { titulo, mensaje, textoConfirmar, accion }
    // 29-oct-2026: sin mes ni período pedido, abre en el período abierto (desde el día siguiente a lo último
    // facturado hasta hoy), que es como se le factura a la empresa; el mes calendario queda a un toque
    const autoPeriodo = useRef(!mesInicial && !periodoInicial);

    const cargar = useCallback(async () => {
        setCargando(true);
        try {
            const [c, a] = await Promise.all([
                api.get(`/clientes/${cliente.id}/cierre-mensual`, { params: periodo || { mes } }),
                // La aprobación de la empresa es por mes del portal
                periodo ? Promise.resolve({ data: null })
                    : api.get('/pedidos-empresa/aprobacion', { params: { clienteId: cliente.id, mes } }).catch(() => ({ data: null })),
            ]);
            if (autoPeriodo.current) {
                autoPeriodo.current = false;
                const sug = c.data?.sugeridoDesde;
                const hoy = getTodayISO();
                if (sug && sug <= hoy && (c.data.filas || []).some(f => f.estado === 'COBRADO' || f.estado === 'FACTURADO' || f.estado === 'REALIZADO')) {
                    setPeriodo({ desde: String(sug).slice(0, 10), hasta: hoy });
                    return;
                }
            }
            setCierre(c.data);
            setAprob(a.data?.estado ? a.data : null);
        } catch { toast.error('No se pudo calcular el mes'); }
        finally { setCargando(false); }
    }, [cliente.id, mes, periodo]);
    useEffect(() => { setCierre(null); cargar(); }, [cargar]);
    // Quien abrió el cierre se acuerda del mes (al volver de un trabajo, vuelve al mismo mes)
    useEffect(() => { onMes && onMes(mes); }, [mes]); // eslint-disable-line react-hooks/exhaustive-deps

    // Estados de los trabajos del mes (uno por trabajo, no por equipo)
    const estados = (() => {
        if (!cierre) return null;
        const porServ = {};
        cierre.filas.forEach(f => { porServ[f.servicioId] = f.estado; });
        const v = Object.values(porServ);
        return {
            sinFacturar: v.filter(e => e === 'COMPLETADO' || e === 'PENDIENTE_FACTURACION').length,
            facturados: v.filter(e => e === 'FACTURADO').length,
            cobrados: v.filter(e => e === 'COBRADO' || e === 'REALIZADO').length,
        };
    })();
    const marcar = async (estado) => {
        setMarcando(true);
        try {
            const r = await api.post(`/clientes/${cliente.id}/cierre-mensual/marcar`, { estado, factura }, { params: periodo || { mes } });
            const n = r.data.actualizados, s = n !== 1 ? 's' : '';
            toast.success({ FACTURADO: `${n} trabajo${s} facturado${s}`, COBRADO: `${n} trabajo${s} cobrado${s}`,
                DESHACER_FACTURA: 'Facturación deshecha: vuelve a "sin facturar"', DESHACER_COBRO: 'Cobro deshecho' }[estado]);
            await cargar();
            onCambio && onCambio();   // que la lista de trabajos de atrás no quede con el estado viejo
        } catch (e) { toast.error(e?.response?.data?.mensaje || 'No se pudo actualizar'); }
        finally { setMarcando(false); }
    };

    // Respuesta a un renglón observado por la empresa (15-oct-2026)
    const responder = async (itemId, tipo, texto) => {
        try {
            const r = await api.post('/pedidos-empresa/aprobacion/responder', { clienteId: cliente.id, mes, itemId, tipo, texto });
            setAprob(r.data?.estado ? r.data : null);
            toast.success(tipo === 'QUITADO' ? 'Sacado del resumen' : 'Respuesta guardada');
            if (tipo === 'QUITADO') { cargar(); onCambio && onCambio(); }
        } catch (e) { toast.error(e?.response?.data?.mensaje || 'No se pudo guardar'); throw e; }
    };
    // Deshacer lo que respondió la empresa (aprobado u observado): vuelve a "sin revisar" (15-oct-2026)
    const reabrir = async () => {
        try {
            await api.delete('/pedidos-empresa/aprobacion', { params: { clienteId: cliente.id, mes } });
            setAprob(null);
            toast.success('Vuelve a "sin revisar": le avisamos a la empresa');
        } catch (e) { toast.error(e?.response?.data?.mensaje || 'No se pudo deshacer'); }
    };
    const mesNombre = periodo ? `del ${ddmm(periodo.desde)} al ${ddmm(periodo.hasta)}` : nombreMes(mes).split(' ')[0].toLowerCase();
    const sinResponder = (aprob?.observados || []).filter(id => !(aprob?.respuestas || {})[String(id)]).length;

    // Lo que se muestra arriba y se factura: si el mes mezcla cobrados y sin facturar, solo lo pendiente
    const pend = cierre?.pendiente || null;
    const tot = pend || cierre;
    const siguiente = cierre?.tramos?.find(t => t.desde > tot.cantidadEquipos);
    const esActual = mes === mesActual();
    const mesEnCurso = esActual && !periodo;
    const hay = cierre && cierre.cantidadEquipos > 0;

    // Pie: el próximo paso del mes, uno solo
    let pie = null;
    if (hay && estados) {
        if (estados.sinFacturar > 0) {
            pie = (
                <div className="space-y-2">
                    <div className="flex gap-1.5">
                        <input value={factura} onChange={e => setFactura(e.target.value)} placeholder="N° de factura (opcional)"
                            className="flex-1 min-w-0 h-11 px-3 rounded-xl bg-panel text-ink text-body font-bold outline-none placeholder:text-muted placeholder:font-medium" />
                        <button type="button" onClick={() => generarPDFCierreMensual(cierre)} aria-label="Descargar PDF del mes"
                            className="h-11 px-3 shrink-0 rounded-xl bg-chip text-ink text-label font-black flex items-center gap-1.5 active:scale-95"><LuFileText size={15} /> PDF</button>
                    </div>
                    {sinResponder > 0 && <p className="text-caption font-bold text-brand-red text-center">{sinResponder} renglón{sinResponder !== 1 ? 'es' : ''} que la empresa no reconoce, sin responder</p>}
                    <button type="button" disabled={marcando || !tot.precioUnitario}
                        onClick={() => setConfirmar({
                            titulo: `¿Facturar ${mesNombre}?`,
                            mensaje: `${estados.sinFacturar} trabajo${estados.sinFacturar !== 1 ? 's' : ''} · ${fmt(tot.total)}${factura.trim() ? ` · Factura ${factura.trim()}` : ''}. Si te equivocás, lo podés deshacer.`,
                            textoConfirmar: 'Sí, facturar', accion: () => marcar('FACTURADO'),
                        })}
                        className="w-full h-12 rounded-xl bg-brand-red text-white text-label font-black uppercase active:scale-95 disabled:opacity-40">
                        {marcando ? 'Guardando…' : `Facturar ${pend ? 'lo pendiente' : mesNombre} · ${fmt(tot.total)}`}
                    </button>
                    {!tot.precioUnitario && <p className="text-caption font-bold text-brand-red text-center">Cargá la tarifa para poder facturar.</p>}
                    {mesEnCurso && <p className="text-caption text-muted text-center">El mes todavía no terminó: si facturás ahora, lo que se cargue después va aparte.</p>}
                </div>
            );
        } else if (estados.facturados > 0) {
            pie = (
                <div className="space-y-2">
                    <div className="flex gap-1.5">
                    <button type="button" onClick={() => generarPDFCierreMensual(cierre)} className="h-12 px-4 rounded-xl bg-chip text-ink text-label font-black flex items-center gap-1.5 active:scale-95"><LuFileText size={15} /> PDF</button>
                    <button type="button" onClick={() => marcar('COBRADO')} disabled={marcando}
                        className="flex-1 h-12 rounded-xl bg-[#16A34A] text-white text-label font-black uppercase active:scale-95 disabled:opacity-40">{marcando ? 'Guardando…' : 'Ya me pagaron'}</button>
                    </div>
                    <button type="button" onClick={() => setConfirmar({
                        titulo: 'Deshacer la facturación', mensaje: `Los trabajos de ${mesNombre} vuelven a "sin facturar" y se borra el N° de factura cargado.`,
                        textoConfirmar: 'Sí, deshacer', accion: () => marcar('DESHACER_FACTURA'),
                    })} className="w-full text-label font-black text-secondary underline">Deshacer la facturación</button>
                </div>
            );
        } else {
            pie = (
                <div className="flex gap-1.5 items-center">
                    <p className="flex-1 flex items-center gap-1.5 text-body font-black text-[#16A34A]"><LuCircleCheck size={18} /> Mes cobrado</p>
                    <button type="button" onClick={() => setConfirmar({
                        titulo: 'Deshacer el cobro', mensaje: `Los trabajos de ${mesNombre} vuelven a "facturado, esperando el pago".`,
                        textoConfirmar: 'Sí, deshacer', accion: () => marcar('DESHACER_COBRO'),
                    })} className="h-11 px-3 text-label font-black text-secondary underline">Deshacer</button>
                    <button type="button" onClick={() => generarPDFCierreMensual(cierre)} className="h-11 px-4 rounded-xl bg-chip text-ink text-label font-black flex items-center gap-1.5 active:scale-95"><LuFileText size={15} /> PDF</button>
                </div>
            );
        }
    }

    return (
        <>
        <ModalShell titulo={cliente.nombre || cierre?.cliente?.nombre || "Cliente"} subtitulo="Cierre mensual" onCerrar={onClose} pie={pie} ancho="md:max-w-2xl">
            <div className="space-y-4">
                {/* Mes o período con fechas */}
                <PeriodoCierre mes={mes} onMes={setMes} periodo={periodo} onPeriodo={setPeriodo} esActual={esActual} sugeridoDesde={cierre?.sugeridoDesde} />

                {/* 1. Total del mes */}
                {cargando && !cierre ? <p className="text-caption text-muted text-center py-6">Calculando…</p> : cierre && (
                    <div className="rounded-2xl bg-panel p-4">
                        <div className="flex items-end justify-between gap-3">
                            <div>
                                <p className="text-caption text-muted">{pend ? `${pend.cantidadEquipos} equipo${pend.cantidadEquipos !== 1 ? 's' : ''} sin facturar (desde el ${ddmm(String(pend.desde).slice(0, 10))})`
                                    : `${cierre.cantidadEquipos} equipo${cierre.cantidadEquipos !== 1 ? 's' : ''} en ${cierre.cantidadServicios} visita${cierre.cantidadServicios !== 1 ? 's' : ''}`}
                                    {cierre.reclamosSinCargo > 0 && <span className="block text-[var(--etapa-listo-tx)] font-bold">+ {cierre.reclamosSinCargo} revisita{cierre.reclamosSinCargo !== 1 ? 's' : ''} por reclamo sin cargo (no suma{cierre.reclamosSinCargo !== 1 ? 'n' : ''})</span>}</p>
                                <p className="text-caption text-secondary">{tot.precioUnitario ? `${fmt(tot.precioUnitario)} + IVA por equipo` : 'Sin tarifa cargada'}</p>
                            </div>
                            <div className="text-right">
                                <p className="text-label font-black text-muted uppercase">{estados?.sinFacturar ? 'A facturar' : 'Total'}</p>
                                <p className="text-title font-black text-ink leading-none">{fmt(tot.total)}</p>
                            </div>
                        </div>
                        {siguiente && (
                            <div className="mt-3">
                                <div className="h-1.5 rounded-full bg-chip overflow-hidden">
                                    <div className="h-full bg-brand-amber" style={{ width: `${Math.min(100, (tot.cantidadEquipos / siguiente.desde) * 100)}%` }} />
                                </div>
                                <p className="mt-1 text-label text-muted">Faltan {siguiente.desde - tot.cantidadEquipos} equipos para el tramo de {fmt(siguiente.precio)}</p>
                            </div>
                        )}
                        <details className="mt-2 text-caption text-secondary">
                            <summary className="cursor-pointer text-label font-bold text-muted">Ver el detalle</summary>
                            <div className="pt-1.5 space-y-0.5">
                                {[['Mano de obra', tot.manoDeObra], ['Repuestos (sin IVA)', tot.repuestosNeto], ['Subtotal', tot.subtotal], ['IVA 21%', tot.iva]].map(([l, v]) => (
                                    <div key={l} className="flex justify-between"><span>{l}</span><span className="font-bold">{fmt(v)}</span></div>
                                ))}
                            </div>
                        </details>
                        {estados && hay && (
                            <p className="mt-2 text-label text-muted">
                                {[estados.sinFacturar && `${estados.sinFacturar} sin facturar`, estados.facturados && `${estados.facturados} facturado${estados.facturados !== 1 ? 's' : ''}`, estados.cobrados && `${estados.cobrados} cobrado${estados.cobrados !== 1 ? 's' : ''}`].filter(Boolean).join(' · ')}
                            </p>
                        )}
                    </div>
                )}

                {/* 2. Qué dijo la empresa */}
                {hay && !periodo && <div><p className={PASO}>La empresa</p><EstadoEmpresa aprob={aprob} onDeshacer={() => setConfirmar({
                    titulo: 'Volver a "sin revisar"', mensaje: `Se borra lo que respondió la empresa sobre ${mesNombre} y le llega un aviso para que lo revise de nuevo.`,
                    textoConfirmar: 'Sí, volver', accion: reabrir,
                })} /></div>}

                {/* 3. Equipos del mes */}
                {cierre && (
                    <div>
                        <p className={PASO}>Equipos del mes</p>
                        <EquiposDelMes filas={cierre.filas} observados={aprob?.observados || []} respuestas={aprob?.respuestas || {}}
                            onAbrir={onAbrirServicio ? (id) => onAbrirServicio(id) : null}
                            onResponder={responder}
                            onCorregir={onEditarServicio ? (id) => onEditarServicio(id) : null} />
                    </div>
                )}

                {/* 4. Repuestos que compraste para la empresa (no cargados en un trabajo) */}
                {cierre && (
                    <div>
                        <p className={PASO}>Repuestos comprados</p>
                        <RepuestosComprados clienteId={cliente.id} lista={cierre.repuestosExtra || []} onCambio={() => { cargar(); onCambio && onCambio(); }} />
                    </div>
                )}

                {/* Tarifa: se cambia poco */}
                <TarifaTramos clienteId={cliente.id} onGuardada={cargar} />
            </div>
        </ModalShell>
            {confirmar && (
                <ConfirmDialog titulo={confirmar.titulo} mensaje={confirmar.mensaje} textoConfirmar={confirmar.textoConfirmar}
                    onCancelar={() => setConfirmar(null)}
                    onConfirmar={() => { const a = confirmar.accion; setConfirmar(null); a(); }} />
            )}
        </>
    );
}
